import { inject, Inject, Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

import {
  IMPERSONATED_USER_EMAIL,
  IMPERSONATED_USER_INFO_TOKEN,
  IMPERSONATE_AUTH_TOKEN,
  IS_IMPERSONATION_MODE_ON,
  USERID_TOKEN,
} from './logged-in.service';
import { LoginRedirectService } from './login-redirect.service';
import { SsoOptionsService } from './sso-options.service';
import { isImpersonationModeOn } from '@miq/utils';

const UNAUTHORIZED_HTTP_STATUS = 401;
const isUnauthorizedHttpStatus = ({ status }) => status === UNAUTHORIZED_HTTP_STATUS;

/**
 * The SSO endpoints themselves must never be handled by this interceptor.
 *
 * `/confirm` is the call that MINTS the session, so attaching a stale bearer to
 * it is meaningless, and letting the 401 branch below act on its response is
 * actively harmful: it fires a blind top-level redirect to `/login` that skips
 * AsdCallbackComponent's error handling entirely (the component's `error`
 * callback never runs, because the interceptor swallows the failure into
 * EMPTY). `alreadyRedirected` is per-page-instance, so a full page load resets
 * it - which makes that path an unbounded login redirect loop rather than a
 * one-shot recovery. The backend already performs its own recovery for a failed
 * exchange (a 307 to the identity provider), so the FE must stay out of it.
 */
function isSsoEndpoint(url: string, ssoOptions: SsoOptionsService): boolean {
  const { tokenExchangeUrl, redirectUrl } = ssoOptions.options;
  return [tokenExchangeUrl, redirectUrl].some(ssoUrl => !!ssoUrl && url.startsWith(ssoUrl));
}

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  constructor(
    private locationState: LoginRedirectService,
    private ssoOptions: SsoOptionsService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Checked BEFORE alreadyRedirected: the token exchange must reach the
    // component even on a page where a redirect has already been flagged.
    if (isSsoEndpoint(request.url, this.ssoOptions)) {
      return next.handle(request);
    }
    if (this.ssoOptions.alreadyRedirected) {
      return EMPTY; // returning EMPTY to prevent multiple redirection.
    }
    const isImpersonatedModeOn = isImpersonationModeOn();
    const userIdToken = localStorage.getItem(isImpersonatedModeOn ? IMPERSONATE_AUTH_TOKEN : USERID_TOKEN);
    if (userIdToken && !request.headers.has('Authorization')) {
      const headers = request.headers.set('Authorization', `BEARER ${userIdToken}`);
      const clonedRequest = request.clone({ headers });
      return next.handle(clonedRequest).pipe(catchError(this.isUnauthorizedResponse.bind(this)));
    }
    return next.handle(request).pipe(catchError(this.isUnauthorizedResponse.bind(this)));
  }

  private isUnauthorizedResponse(response: any) {
    if (
      response instanceof HttpErrorResponse &&
      isUnauthorizedHttpStatus(response) &&
      !this.ssoOptions.alreadyRedirected
    ) {
      this.logger.error({
        eventName: 'request unauthorized',
        requestUrl: response.url,
        status: response.status,
      });
      this.ssoOptions.alreadyRedirected = true;
      this.resetImpersonation();
      // Deferred: any HTTP call app-wide can 401 around the same time as a route guard's own
      // redirect (e.g. on a fresh unauthenticated load). Guards resolve state.url synchronously,
      // so waiting a microtask here ensures their precise redirect always wins the in-flight
      // lock in LoginRedirectService over this best-effort, document.location-based one.
      queueMicrotask(() => this.locationState.redirectWithState());
      return EMPTY;
    }
    return throwError(() => response);
  }

  private resetImpersonation() {
    localStorage.setItem(IS_IMPERSONATION_MODE_ON, 'false');
    localStorage.setItem(IMPERSONATE_AUTH_TOKEN, null);
    localStorage.setItem(IMPERSONATED_USER_INFO_TOKEN, null);
    localStorage.setItem(IMPERSONATED_USER_EMAIL, null);
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const ssoOptions = inject(SsoOptionsService);
  const locationState = inject(LoginRedirectService);
  const logger = inject(CbreLoggerToken);

  // Checked BEFORE alreadyRedirected: the token exchange must reach the
  // component even on a page where a redirect has already been flagged.
  if (isSsoEndpoint(req.url, ssoOptions)) {
    return next(req);
  }
  if (ssoOptions.alreadyRedirected) {
    return EMPTY;
  }
  const isImpersonatedModeOn = isImpersonationModeOn();
  const userIdToken = localStorage.getItem(isImpersonatedModeOn ? IMPERSONATE_AUTH_TOKEN : USERID_TOKEN);
  const handleUnauthorized = (response: any): Observable<never> => {
    if (
      response instanceof HttpErrorResponse &&
      response.status === UNAUTHORIZED_HTTP_STATUS &&
      !ssoOptions.alreadyRedirected
    ) {
      logger.error({
        eventName: 'request unauthorized',
        requestUrl: response.url,
        status: response.status,
      });
      ssoOptions.alreadyRedirected = true;
      localStorage.setItem(IS_IMPERSONATION_MODE_ON, 'false');
      localStorage.setItem(IMPERSONATE_AUTH_TOKEN, null);
      localStorage.setItem(IMPERSONATED_USER_INFO_TOKEN, null);
      localStorage.setItem(IMPERSONATED_USER_EMAIL, null);
      // Deferred - see isUnauthorizedResponse above for why this can't redirect synchronously.
      queueMicrotask(() => locationState.redirectWithState());
      return EMPTY;
    }
    return throwError(() => response);
  };
  if (userIdToken && !req.headers.has('Authorization')) {
    const clonedRequest = req.clone({ headers: req.headers.set('Authorization', `BEARER ${userIdToken}`) });
    return next(clonedRequest).pipe(catchError(handleUnauthorized));
  }
  return next(req).pipe(catchError(handleUnauthorized));
};
